import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Input,
  Tooltip,
  Modal,
  Checkbox,
  notification,
  Alert,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import axiosInstance from "../../../core/index-core";
import { metrics } from "../../../metrics/client";
import { getNoteCommentAPI, saveNoteCommentAPI } from "../../../services/api";
import { ERROR_CODES, LOCK_REASONS, ERROR_MESSAGES, TOOLTIP_TEXT, WARNING_MESSAGES } from "./constants";
import { useAiNotePolling } from "../../../hooks/useAiNotePolling";

const { TextArea } = Input;

const ManualNotes = ({
  generateAINotesClicked,
  noteCommentSession_id,
  patient_id,
  onNotesChange,
  notes,
  onAiNoteReady,
  setPollingNote,
  noteSubmitted,
  setGeneratingNotes,
  isNoteSigned,
  generatingNotes,
  pollingNote = false, // Whether SessionRightPanel is polling for AI note (e.g., auto-poll on 202)
  setNoteJSON,
  setActiveTab,
  onLockStateChange,
  hasRecordingStarted,
  connectionState = 'connected', // Connection state prop (derived UI state)
  isOnline = true, // Network status prop
  isValidAppointment = true, // Valid appointment flag
}) => {
  const isOffline = !isOnline; // Use network status for disable logic
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const [generateError, setGenerateError] = useState(null);

  // Lock state management
  const [noteIsLocked, setNoteIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState(null);
  const [lockedAt, setLockedAt] = useState(null);

  // Call onLockStateChange callback when lock state transitions to true
  useEffect(() => {
    if (noteIsLocked && onLockStateChange) {
      onLockStateChange(true);
    }
  }, [noteIsLocked, onLockStateChange]);

  // Set lock state when note is submitted successfully
  useEffect(() => {
    if (noteSubmitted && !noteIsLocked) {
      setNoteIsLocked(true);
      setLockReason(LOCK_REASONS.EHR_SUBMITTED);
      setLockedAt(new Date());
    }
  }, [noteSubmitted, noteIsLocked]);

  const handleNoteReady = useCallback((noteData) => {
    setNoteJSON(noteData);
    if (noteData?.ehr_submitted) {
      setNoteIsLocked(true);
      setLockReason(LOCK_REASONS.EHR_SUBMITTED);
      setLockedAt(new Date());
    }
    setNoteSaving(false);
    setPollingNote(false);
  }, [setNoteJSON, setPollingNote]);

  const handleGenerationFailed = useCallback((statusData) => {
    const errCode = statusData?.error_code;
    const errMsg = statusData?.error || "AI note generation failed.";
    if (errCode === ERROR_CODES.NOTE_LOCKED_EHR_SUBMITTED || errCode === ERROR_CODES.NOTE_LOCKED_SIGNED) {
      setNoteIsLocked(true);
      setLockReason(errCode === ERROR_CODES.NOTE_LOCKED_EHR_SUBMITTED ?
        LOCK_REASONS.EHR_SUBMITTED : LOCK_REASONS.NOTE_SIGNED);
      setLockedAt(new Date());
      notification.error(ERROR_MESSAGES.LOCK_ERROR);
    } else {
      setGenerateError(errMsg);
      notification.error({
        message: "AI Note generation failed",
        description: errMsg,
      });
    }
    setNoteSaving(false);
    setPollingNote(false);
  }, [setPollingNote]);

  const { startPolling, stopPolling, fetchNote } = useAiNotePolling({
    patientId: patient_id,
    sessionId: noteCommentSession_id,
    onNoteReady: handleNoteReady,
    onFailed: handleGenerationFailed,
  });

  // 🔔 nudge UI
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [glowOnce, setGlowOnce] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const countdownRef = useRef(null);
  const buttonRef = useRef(null);

  // one-time glow CSS
  const GlowStyles = (
    <style>{`
      @keyframes aiPulseOnce {
        0% { box-shadow: 0 0 0px rgba(22,119,255,0); transform: translateZ(0); }
        30% { box-shadow: 0 0 18px rgba(22,119,255,0.65); }
        100% { box-shadow: 0 0 0px rgba(22,119,255,0); }
      }
      .aiPulseOnce { animation: aiPulseOnce 1200ms ease-out 1; }
    `}</style>
  );

  const startNudge = useCallback(() => {
    setNudgeOpen(true);
    setGlowOnce(true);
    setSecondsLeft(60);

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    countdownRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          setNudgeOpen(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    setTimeout(() => setGlowOnce(false), 1250);
  }, []);

  const closeNudge = useCallback(() => {
    setNudgeOpen(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  useEffect(() => {
    const onNudge = () => {
      if (noteSubmitted) return;
      startNudge();
    };
    window.addEventListener("ai-notes:nudge", onNudge);
    return () => {
      window.removeEventListener("ai-notes:nudge", onNudge);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [noteSubmitted, startNudge]);

  const confirmKey = `aiNotesGenConfirmDismissed:${patient_id}:${noteCommentSession_id}`;
  useEffect(() => {
    const saved = localStorage.getItem(confirmKey);
    if (saved === "true") setDontAskAgain(true);
  }, [confirmKey]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const fetchNoteComment = useCallback(async () => {
    if (!patient_id || !noteCommentSession_id) return;
    setNoteLoading(true);
    try {
      const note = await getNoteCommentAPI(patient_id, noteCommentSession_id);
      onNotesChange?.(note?.data || "");
    } catch (e) {
      console.error(e);
    } finally {
      setNoteLoading(false);
    }
  }, [onNotesChange, patient_id, noteCommentSession_id]);

  const triggerGenerateAiNotes = useCallback(async () => {
    if (!patient_id || !noteCommentSession_id) return { ok: false, status: 0 };
    try {
      const res = await axiosInstance.post(
        `patient/${patient_id}/sessions/${noteCommentSession_id}/generate-ai-notes`,
        {},
        { validateStatus: (status) => status !== 401 }
      );
      const body = res?.data ?? {};

      // Check for lock error using error codes
      const lockErrorCode = body?.error_code;
      const isNoteLocked = res.status === 400 && (
        lockErrorCode === ERROR_CODES.NOTE_LOCKED_EHR_SUBMITTED ||
        lockErrorCode === ERROR_CODES.NOTE_LOCKED_SIGNED
      );

      if (isNoteLocked) {
        setNoteIsLocked(true);
        setLockReason(lockErrorCode === ERROR_CODES.NOTE_LOCKED_EHR_SUBMITTED ?
          LOCK_REASONS.EHR_SUBMITTED : LOCK_REASONS.NOTE_SIGNED);
        setLockedAt(new Date());
        notification.error(ERROR_MESSAGES.LOCK_ERROR);
        // Telemetry: note generation blocked by lock
        metrics.add(metrics.noteGenerationErrorEvent({
          session_id: noteCommentSession_id,
          patient_id,
          reason: ERROR_MESSAGES.LOCK_ERROR.description,
          error_code: lockErrorCode,
        }));
        metrics.flush(); // Flush immediately — high-value domain error event
        return { ok: false, status: res.status };
      }

      // Check for unsupported template error
      if (res.status === 400 && lockErrorCode === ERROR_CODES.UNSUPPORTED_TEMPLATE) {
        notification.error({
          message: ERROR_MESSAGES.UNSUPPORTED_TEMPLATE.message,
          description: body?.reason || ERROR_MESSAGES.UNSUPPORTED_TEMPLATE.description,
        });
        return { ok: false, status: res.status };
      }

      // Check for unsupported session type error
      if (lockErrorCode === ERROR_CODES.UNSUPPORTED_SESSION_TYPE || lockErrorCode === 'UNSUPPORTED_SESSION_TYPE') {
        notification.error({
          message: ERROR_MESSAGES.UNSUPPORTED_SESSION_TYPE?.message || 'Unsupported Appointment Type',
          description: body?.reason || body?.message || ERROR_MESSAGES.UNSUPPORTED_SESSION_TYPE?.description || 'This appointment type is not supported for AI note generation.',
        });
        window.dispatchEvent(new CustomEvent('session:unsupported-type'));
        return { ok: false, status: res.status };
      }

      // Check for transcript quality error (HTTP 422)
      const transcriptErrorCode = body?.detail?.error_code;
      if (res.status === 422 && transcriptErrorCode === ERROR_CODES.TRANSCRIPT_QUALITY_TOO_LOW) {
        notification.error(ERROR_MESSAGES.TRANSCRIPT_QUALITY_TOO_LOW);
        // Telemetry: transcript quality too low
        metrics.add(metrics.noteGenerationErrorEvent({
          session_id: noteCommentSession_id,
          patient_id,
          reason: ERROR_MESSAGES.TRANSCRIPT_QUALITY_TOO_LOW.description,
          error_code: ERROR_CODES.TRANSCRIPT_QUALITY_TOO_LOW,
        }));
        metrics.flush(); // Flush immediately — high-value domain error event
        return { ok: false, status: res.status };
      }

      // Check for server errors (5xx)
      if (res.status >= 500) {
        const msg = ERROR_MESSAGES.SERVER_ERROR.description;
        notification.error(ERROR_MESSAGES.SERVER_ERROR);
        setGenerateError(msg);
        metrics.add(metrics.noteGenerationErrorEvent({
          session_id: noteCommentSession_id,
          patient_id,
          reason: `Server error ${res.status}`,
        }));
        metrics.flush();
        return { ok: false, status: res.status };
      }

      // 202 Accepted — async generation started, return generation_id for status polling
      if (res.status === 202 && body?.status === "accepted" && body?.generation_id) {
        setGenerateError(null);
        return { ok: true, status: 202, generation_id: body.generation_id };
      }

      if (body?.status === "success") {
        setGenerateError(null);

        // Check for warning in successful response (e.g., unsupported template auto-fill)
        if (body?.warning?.warning_code === ERROR_CODES.UNSUPPORTED_TEMPLATE_AUTO_FILL) {
          notification.warning({
            message: WARNING_MESSAGES.UNSUPPORTED_TEMPLATE_AUTO_FILL.message,
            description: WARNING_MESSAGES.UNSUPPORTED_TEMPLATE_AUTO_FILL.description,
            duration: 10,
          });
        }

        return { ok: true, status: res.status ?? 200 };
      }
      if (body?.error) {
        notification.error({
          message: "Couldn't start AI notes",
          description: String(body.error),
        });
        setGenerateError(String(body.error));
        // Telemetry: generic note generation error from server
        metrics.add(metrics.noteGenerationErrorEvent({
          session_id: noteCommentSession_id,
          patient_id,
          reason: String(body.error),
        }));
        metrics.flush(); // Flush immediately — high-value domain error event
      } else {
        notification.error({
          message: "Couldn't start AI notes",
          description: `Server returned ${res.status}.`,
        });
        setGenerateError("Something went wrong.");
        // Telemetry: unexpected status code
        metrics.add(metrics.noteGenerationErrorEvent({
          session_id: noteCommentSession_id,
          patient_id,
          reason: `Server returned ${res.status}`,
        }));
        metrics.flush(); // Flush immediately — high-value domain error event
      }
      return { ok: false, status: res.status };
    } catch (e) {
      notification.error(ERROR_MESSAGES.NETWORK_ERROR);
      setGenerateError("Network error. Please check your connection.");
      // Telemetry: network error during note generation
      metrics.add(metrics.noteGenerationErrorEvent({
        session_id: noteCommentSession_id,
        patient_id,
        reason: e?.message || 'Network error',
      }));
      metrics.flush(); // Flush immediately — high-value domain error event
      return { ok: false, status: 0 };
    }
  }, [patient_id, noteCommentSession_id]);

  const proceedGenerateAiNotes = useCallback(async () => {
    if (!patient_id || !noteCommentSession_id) return;
    setGenerateError(null);
    setNoteSaving(true);
    setPollingNote(true);
    setActiveTab("3")

    try {
      await saveNoteCommentAPI(patient_id, noteCommentSession_id, notes ?? "");
      setLastSavedAt(new Date());

      // close tooltip when user acts
      closeNudge();

      const startRes = await triggerGenerateAiNotes();
      if (!startRes.ok) {
        setNoteSaving(false);
        setPollingNote(false);
        return;
      }

      startPolling({ generationId: startRes.generation_id ?? null });
    } catch (e) {
      console.error(e);
      stopPolling();
      setNoteSaving(false);
      setPollingNote(false);
      notification.error({
        message: "Unable to generate AI notes",
        description: "Saving notes or starting generation failed.",
      });
      setGenerateError("Saving notes or starting generation failed.");
      // Telemetry: proceedGenerateAiNotes outer catch
      metrics.add(metrics.noteGenerationErrorEvent({
        session_id: noteCommentSession_id,
        patient_id,
        reason: e?.message || 'Saving notes or starting generation failed',
      }));
      metrics.flush(); // Flush immediately — high-value domain error event
    }
  }, [
    notes,
    patient_id,
    noteCommentSession_id,
    startPolling,
    stopPolling,
    triggerGenerateAiNotes,
    setPollingNote,
    closeNudge,
  ]);

  const handleGenerateClick = useCallback(() => {
    generateAINotesClicked();
    const skip = localStorage.getItem(confirmKey) === "true";
    closeNudge();
    if (skip) {
      proceedGenerateAiNotes();
    } else {
      setConfirmOpen(true);
    }
  }, [confirmKey, proceedGenerateAiNotes]);

  const handleConfirmOk = () => {
    if (dontAskAgain) localStorage.setItem(confirmKey, "true");
    setConfirmOpen(false);
    proceedGenerateAiNotes();
  };
  const handleConfirmCancel = () => setConfirmOpen(false);

  useEffect(() => {
    if (patient_id && noteCommentSession_id && !notes) fetchNoteComment();
  }, [patient_id, noteCommentSession_id, fetchNoteComment]);

  // Check AI note lock status on component mount
  useEffect(() => {
    if (patient_id && noteCommentSession_id) {
      const checkLock = async () => {
        const result = await fetchNote();
        if (result.failed) {
          setGenerateError(result.error || "AI note generation failed.");
          return;
        }
        if (result.status === 200 && result.noteData) {
          setNoteJSON(result.noteData);
          if (result.noteData?.ehr_submitted) {
            setNoteIsLocked(true);
            setLockReason(LOCK_REASONS.EHR_SUBMITTED);
            setLockedAt(new Date());
          }
        }
      };
      checkLock();
    }
  }, [patient_id, noteCommentSession_id, fetchNote]);

  // Reset lock state, error state, and stop polling when session changes
  useEffect(() => {
    setNoteIsLocked(false);
    setLockReason(null);
    setLockedAt(null);
    setGenerateError(null);
    stopPolling();
  }, [noteCommentSession_id, patient_id, stopPolling]);

  useEffect(() => {
    if (!setGeneratingNotes) return;
    if (noteLoading || noteSaving) setGeneratingNotes(true);
    else setGeneratingNotes(false);
  }, [noteSaving, noteLoading, setGeneratingNotes]);

  const tooltipContent = (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          Generate AI Notes
        </div>
        <div style={{ fontSize: 12, opacity: 0.9 }}>
          Transcript is ready. Click to generate.
        </div>
      </div>
      <button
        onClick={closeNudge}
        style={{
          border: 0,
          background: "transparent",
          cursor: "pointer",
          lineHeight: 1,
          padding: 0,
          fontSize: 14,
        }}
        aria-label="Close"
        title="Close"
      >
        ×
      </button>
    </div>
  );


  return (
    <div
      className="flex-1 bg-white flex flex-col gap-4 min-h-0"
      style={{ height: "98%" }}
    >
      {GlowStyles}

      {/* Header */}
      <div className="flex items-center justify-between pb-3" style={{
        padding: '20px 20px 7px',
        borderBottom: '2.5px solid rgb(226, 232, 240)'
      }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3
            className="font-semibold text-gray-800 text-[15px] tracking-wide uppercase m-0"
            style={{ letterSpacing: "0.5px" }}
          >
            Notes
          </h3>
          {/* <Tooltip title="Any manual updates to the AI Notes will be reset">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path d="m9,9 a3,3 0 1,1 6,0c0,2 -3,3 -3,3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="m9,17 h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Tooltip> */}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-5">
        <TextArea
          className="flex-1 resize-none border border-gray-200 rounded-lg"
          value={notes}
          disabled={
            noteSubmitted ||
            noteSaving ||
            noteLoading ||
            generatingNotes ||
            isNoteSigned ||
            pollingNote
          }
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Include any additional details as notes here. The AI notes will be generated combining the notes and transcription."
          style={{
            backgroundColor: "#fafbfc",
            padding: "12px 16px",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        />
        {generateError && (
          <Alert
            message="AI note generation failed"
            description={generateError}
            type="error"
            showIcon
            closable
            onClose={() => setGenerateError(null)}
            style={{ marginTop: 15 }}
          />
        )}
        {isNoteSigned ? (
          <Alert
            message="This AI Note is signed"
            type="info"
            style={{ marginTop: 15 }}
          />
        ) : (
          <></>
        )}

        <div className="flex justify-end pt-4 mb-2">
          <Tooltip
            open={noteIsLocked || nudgeOpen || isOffline || (isValidAppointment === false) ? true : undefined}
            title={
              noteIsLocked 
                ? TOOLTIP_TEXT.NOTE_LOCKED 
                : isValidAppointment === false
                ? (TOOLTIP_TEXT.INVALID_APPOINTMENT || 'This appointment type is not supported')
                : isOffline
                ? TOOLTIP_TEXT.GENERATE_DISABLED_NO_CONNECTION
                : hasRecordingStarted 
                ? 'Stop recording for generating notes' 
                : tooltipContent
            }
            placement="topRight"
            overlayInnerStyle={{ maxWidth: 260 }}
          >
            <span>
              <Button
              ref={buttonRef}
              type="primary"
              onClick={handleGenerateClick}
              disabled={noteSubmitted || isNoteSigned || noteIsLocked || hasRecordingStarted || isOffline || (isValidAppointment === false) || pollingNote}
              loading={noteSaving || noteLoading || generatingNotes || pollingNote}
              icon={noteIsLocked ? <LockOutlined /> : null}
              className={glowOnce ? "aiPulseOnce" : ""}
              style={{
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
                height: "36px",
                minWidth: "150px",
                ...(nudgeOpen && !noteIsLocked && {
                  boxShadow:
                    "0 0 20px rgba(24, 144, 255, 0.6), 0 0 40px rgba(24, 144, 255, 0.4), 0 0 60px rgba(24, 144, 255, 0.2)",
                  animation: "buttonGlow 1s ease-in-out infinite alternate",
                }),
              }}
              data-testid="btn-generate-ai-notes"
              aria-label="Generate AI Notes"
            >
              {(noteSaving || pollingNote) ? "Generating..." : "Generate AI Notes"}
            </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onOk={handleConfirmOk}
        onCancel={handleConfirmCancel}
        okText="Generate AI Notes"
        cancelText="Cancel"
        centered
        destroyOnClose
      >
        <div className="space-y-3">
          <p style={{ marginBottom: 8 }}>
            When you generate AI Notes, any <strong>manual updates</strong> made
            to the "Generated by AI" fields will be reset.
          </p>
          <Checkbox
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain(e.target.checked)}
          >
            Ok, Don’t show again
          </Checkbox>
        </div>
      </Modal>
    </div>
  );
};

export default ManualNotes;
